import { useState, useEffect, useRef } from 'react';
import { getDashboardCharts } from '@/api/dashboard';
import type { ChartsData }    from '@/api/dashboard';

/* ── Chart.js CDN loader ─────────────────────────────────────── */
declare global { interface Window { Chart: any } }
function loadChartJs(): Promise<void> {
  return new Promise((resolve) => {
    if (window.Chart) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

/* ── Palette ─────────────────────────────────────────────────── */
const TEAL       = '#0F6E56';
const TEAL_LIGHT = 'rgba(15,110,86,0.15)';
const NAVY       = '#1B3A6B';
const NAVY_LIGHT = 'rgba(27,58,107,0.15)';
const AMBER      = '#EF9F27';
const AMBER_LIGHT= 'rgba(239,159,39,0.15)';
const RED        = '#E24B4A';
const RED_LIGHT  = 'rgba(226,75,74,0.15)';
const GRID       = 'rgba(0,0,0,0.06)';
const TICK_COLOR = '#6B7280';
const FONT       = 'Georgia, serif';

const BAR_PALETTE = [
  '#0F6E56','#1B3A6B','#EF9F27','#E24B4A',
  '#534AB7','#D4537E','#639922','#BA7517',
];

/* ── Reusable hook: build/destroy a Chart.js instance ───────── */
function useChart(
  ref: React.RefObject<HTMLCanvasElement>,
  deps: unknown[],
  build: () => any | null,
) {
  const chartRef = useRef<any>(null);
  useEffect(() => {
    if (!ref.current) return;
    loadChartJs().then(() => {
      chartRef.current?.destroy();
      const cfg = build();
      if (cfg) chartRef.current = new window.Chart(ref.current, cfg);
    });
    return () => { chartRef.current?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* ── Chart card wrapper ──────────────────────────────────────── */
function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-grid-border p-5">
      <p className="font-georgia text-sm font-bold text-navy mb-0.5">{title}</p>
      {subtitle && <p className="font-georgia text-xs text-teal mb-4">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

/* ── 1. Gender Donut ─────────────────────────────────────────── */
function GenderDonut({ data }: { data: ChartsData['genderSplit'] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const total = data.reduce((s, d) => s + d.count, 0) || 1;

  useChart(ref, [data], () => ({
    type: 'doughnut',
    data: {
      labels: data.map((d) => d.gender),
      datasets: [{ data: data.map((d) => d.count), backgroundColor: [TEAL, NAVY, AMBER], borderWidth: 0, hoverOffset: 6 }],
    },
    options: {
      responsive: true, maintainAspectRatio: false, cutout: '65%',
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => `  ${c.label}: ${c.parsed} members` } } },
    },
  }));

  return (
    <div className="flex items-center gap-5">
      <div style={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
        <canvas ref={ref} role="img" aria-label="Gender split donut chart">Gender distribution of parish members.</canvas>
      </div>
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={d.gender} className="flex items-center gap-2 text-sm font-georgia">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: [TEAL, NAVY, AMBER][i], flexShrink: 0 }} />
            <span className="text-gray-500">{d.gender}</span>
            <span className="font-bold text-navy ml-1">{d.count}</span>
            <span className="text-gray-400 text-xs">({Math.round((d.count / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 2. Age Distribution Bar ─────────────────────────────────── */
function AgeBar({ data }: { data: ChartsData['ageGroups'] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useChart(ref, [data], () => ({
    type: 'bar',
    data: {
      labels: data.map((d) => d.group),
      datasets: [{ label: 'Members', data: data.map((d) => d.count), backgroundColor: TEAL_LIGHT, borderColor: TEAL, borderWidth: 1.5, borderRadius: 3 }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => `  ${c.parsed.y} members` } } },
      scales: {
        x: { grid: { color: GRID }, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR } },
        y: { grid: { color: GRID }, beginAtZero: true, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR, stepSize: 1 } },
      },
    },
  }));
  return <div style={{ position: 'relative', height: 240 }}><canvas ref={ref} role="img" aria-label="Age distribution bar chart">Age groups of active members.</canvas></div>;
}

/* ── 3. Year-on-year subscription bar (stacked) ──────────────── */
function YearOnYearBar({ data }: { data: ChartsData['yearOnYear'] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useChart(ref, [data], () => ({
    type: 'bar',
    data: {
      labels: data.map((d) => String(d.year)),
      datasets: [
        { label: 'Paid months',   data: data.map((d) => d.paidMonths),   backgroundColor: TEAL,      stack: 'months', borderRadius: 3 },
        { label: 'Unpaid months', data: data.map((d) => d.unpaidMonths), backgroundColor: RED_LIGHT, stack: 'months', borderColor: RED, borderWidth: 1 },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (c: any) => `  ${c.dataset.label}: ${c.parsed.y}` } },
      },
      scales: {
        x: { stacked: true, grid: { color: GRID }, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR } },
        y: { stacked: true, grid: { color: GRID }, beginAtZero: true, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR } },
      },
    },
  }));

  return (
    <>
      <div className="flex gap-4 mb-3">
        {[{ color: TEAL, label: 'Paid months' }, { color: RED, label: 'Unpaid months', light: true }].map(({ color, label, light }) => (
          <div key={label} className="flex items-center gap-1.5 text-xs font-georgia text-gray-500">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: light ? RED_LIGHT : color, border: light ? `1px solid ${color}` : 'none', flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>
      <div style={{ position: 'relative', height: 280 }}>
        <canvas ref={ref} role="img" aria-label="Year-on-year subscription paid vs unpaid stacked bar chart">Paid and unpaid months per year since 2023.</canvas>
      </div>
      {/* Families summary table */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="grid font-georgia text-xs" style={{ gridTemplateColumns: `repeat(${data.length + 1}, 1fr)` }}>
          <span className="text-gray-400">Year</span>
          {data.map((d) => <span key={d.year} className="text-center font-bold text-navy">{d.year}</span>)}
          <span className="text-teal">Paid families</span>
          {data.map((d) => <span key={d.year} className="text-center text-teal">{d.paidFamilies}</span>)}
          <span className="text-red-500">Unpaid families</span>
          {data.map((d) => <span key={d.year} className="text-center text-red-400">{d.unpaidFamilies}</span>)}
        </div>
      </div>
    </>
  );
}

/* ── 4. Families per Anbiyam horizontal bar ──────────────────── */
function AnbiyamBar({ data }: { data: ChartsData['familiesPerAnbiyam'] }) {
  const ref  = useRef<HTMLCanvasElement>(null);
  const top10 = data.slice(0, 10);
  useChart(ref, [data], () => ({
    type: 'bar',
    data: {
      labels: top10.map((d) => d.name),
      datasets: [{ label: 'Families', data: top10.map((d) => d.count), backgroundColor: top10.map((_, i) => BAR_PALETTE[i % BAR_PALETTE.length] + '33'), borderColor: top10.map((_, i) => BAR_PALETTE[i % BAR_PALETTE.length]), borderWidth: 1.5, borderRadius: 3 }],
    },
    options: {
      indexAxis: 'y',
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => `  ${c.parsed.x} families` } } },
      scales: {
        x: { grid: { color: GRID }, beginAtZero: true, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR, stepSize: 1 } },
        y: { grid: { color: GRID }, ticks: { font: { family: FONT, size: 11 }, color: TICK_COLOR } },
      },
    },
  }));
  return <div style={{ position: 'relative', height: Math.max(200, top10.length * 38 + 40) }}><canvas ref={ref} role="img" aria-label="Families per Anbiyam horizontal bar chart">Number of families in each Anbiyam.</canvas></div>;
}

/* ── 5. Parish vs Non-Parish cemetery donut ──────────────────── */
function CemeteryDonut({ data }: { data: ChartsData['cemeteryTypes'] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  useChart(ref, [data], () => ({
    type: 'doughnut',
    data: {
      labels: data.map((d) => d.type),
      datasets: [{ data: data.map((d) => d.count), backgroundColor: [TEAL, NAVY], borderWidth: 0, hoverOffset: 6 }],
    },
    options: { responsive: true, maintainAspectRatio: false, cutout: '65%', plugins: { legend: { display: false } } },
  }));
  return (
    <div className="flex items-center gap-5">
      <div style={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
        <canvas ref={ref} role="img" aria-label="Parish vs Non-Parish cemetery donut">Cemetery record types.</canvas>
      </div>
      <div className="space-y-2">
        {data.map((d, i) => (
          <div key={d.type} className="flex items-center gap-2 text-sm font-georgia">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: [TEAL, NAVY][i], flexShrink: 0 }} />
            <span className="text-gray-500">{d.type}</span>
            <span className="font-bold text-navy ml-1">{d.count}</span>
            <span className="text-gray-400 text-xs">({Math.round((d.count / total) * 100)}%)</span>
          </div>
        ))}
        <p className="font-georgia text-xs text-gray-400 pt-1">Total: {total} records</p>
      </div>
    </div>
  );
}

/* ── 6 & 7. Gauge-style half-donut ──────────────────────────── */
function GaugeChart({
  paid, total, label, colorPaid = TEAL, colorUnpaid = RED,
}: {
  paid: number; total: number; label: string;
  colorPaid?: string; colorUnpaid?: string;
}) {
  const ref  = useRef<HTMLCanvasElement>(null);
  const unpaid = total - paid;
  const pct    = total > 0 ? Math.round((paid / total) * 100) : 0;

  useChart(ref, [paid, total], () => ({
    type: 'doughnut',
    data: {
      labels: ['Paid', 'Unpaid'],
      datasets: [{ data: [paid, Math.max(unpaid, 0)], backgroundColor: [colorPaid, colorUnpaid + '44'], borderWidth: 0 }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      rotation: -90, circumference: 180,
      cutout: '70%',
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c: any) => `  ${c.label}: ${c.parsed}` } } },
    },
  }));

  return (
    <div className="flex flex-col items-center">
      <div style={{ position: 'relative', width: 160, height: 85 }}>
        <canvas ref={ref} role="img" aria-label={`${label} gauge: ${pct}% paid`}>{pct}% paid.</canvas>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center' }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: pct >= 70 ? colorPaid : RED, fontFamily: FONT }}>{pct}%</span>
        </div>
      </div>
      <p className="font-georgia text-xs text-gray-400 mt-2 text-center">{label}</p>
      <div className="flex gap-4 mt-2">
        {[{ color: colorPaid, label: 'Paid', value: paid }, { color: RED, label: 'Unpaid', value: Math.max(unpaid, 0) }].map(({ color, label: l, value }) => (
          <div key={l} className="flex items-center gap-1.5 text-xs font-georgia text-gray-500">
            <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
            {l}: <strong className="text-navy ml-0.5">{value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
export default function ChartsPage() {
  const [data,    setData]    = useState<ChartsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    getDashboardCharts()
      .then(setData)
      .catch(() => setError('Failed to load chart data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-4 md:p-6">
      <h1 className="font-georgia text-2xl font-bold text-navy mb-6">Analytics</h1>
      <p className="font-georgia text-sm text-teal">Loading charts…</p>
    </div>
  );

  if (error || !data) return (
    <div className="p-4 md:p-6">
      <h1 className="font-georgia text-2xl font-bold text-navy mb-6">Analytics</h1>
      <p className="font-georgia text-sm text-red-500">{error || 'No data.'}</p>
    </div>
  );

  const subPct    = data.subscriptionPct;
  const cemSubPct = data.cemeterySubPct;

  return (
    <div className="p-4 md:p-6 w-full">
      <h1 className="font-georgia text-2xl font-bold text-navy mb-1">Analytics</h1>
      <p className="font-georgia text-sm text-teal mb-6">Parish statistics &amp; charts</p>

      {/* Row 1: Gender + Age + Cemetery types */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <ChartCard title="Gender Split" subtitle="Active parish members">
          <GenderDonut data={data.genderSplit} />
        </ChartCard>

        <ChartCard title="Age Distribution" subtitle="Active members by decade">
          <AgeBar data={data.ageGroups} />
        </ChartCard>

        <ChartCard title="Cemetery Records" subtitle="Parish vs Non-Parish">
          <CemeteryDonut data={data.cemeteryTypes} />
        </ChartCard>
      </div>

      {/* Row 2: Year-on-year + Families per Anbiyam (side by side on wide screens) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Subscription Year-on-Year" subtitle="Paid vs unpaid months stacked — 2023 to present">
          <YearOnYearBar data={data.yearOnYear} />
        </ChartCard>

        <ChartCard title="Families per Anbiyam" subtitle="Number of families registered per Anbiyam (top 10)">
          <AnbiyamBar data={data.familiesPerAnbiyam} />
        </ChartCard>
      </div>

      {/* Row 4: Two gauge charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Family Subscription" subtitle={`Families who paid at least one month this year (${new Date().getFullYear()})`}>
          <div className="flex justify-around">
            <GaugeChart
              paid={subPct.paidFamilies}
              total={subPct.totalFamilies}
              label="Families with ≥1 paid month"
            />
            <GaugeChart
              paid={subPct.paidMonths}
              total={subPct.paidMonths + subPct.unpaidMonths}
              label="Paid month-slots this year"
              colorPaid={TEAL}
            />
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-center font-georgia text-xs">
            <div><p className="text-gray-400">Paid families</p><p className="font-bold text-navy">{subPct.paidFamilies}</p></div>
            <div><p className="text-gray-400">Paid months</p><p className="font-bold text-teal">{subPct.paidMonths}</p></div>
            <div><p className="text-gray-400">Unpaid months</p><p className="font-bold text-red-500">{subPct.unpaidMonths}</p></div>
          </div>
        </ChartCard>

        <ChartCard title="Cemetery Subscription %" subtitle={`Cemetery subscription payments for ${new Date().getFullYear()}`}>
          <div className="flex justify-center">
            <GaugeChart
              paid={cemSubPct.paid}
              total={cemSubPct.total}
              label={`${cemSubPct.paid} paid of ${cemSubPct.total} families`}
            />
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-center font-georgia text-xs">
            <div><p className="text-gray-400">Total</p><p className="font-bold text-navy">{cemSubPct.total}</p></div>
            <div><p className="text-gray-400">Paid</p><p className="font-bold text-teal">{cemSubPct.paid}</p></div>
            <div><p className="text-gray-400">Unpaid</p><p className="font-bold text-red-500">{cemSubPct.unpaid}</p></div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
